import { Divider, Typography } from '@material-ui/core'
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined'

import { formatPromotionMinorAmount } from '../../helpers/promotionAmount'
import { getTranslate } from '../../localization'
import { AppliedPromotion, PromotionApplication } from '../../types/cart'
import useStyles from './styles'

/**
 * What the promotion actually did to this booking: which promotions applied, what each was
 * worth, and which room stay each landed on.
 *
 * Ported from wbs-ng, styles included, rather than written afresh. The panel already
 * REQUESTED allocations and rendered none of them — so an agent could see that a discount
 * existed and not what it was worth per room, while the booker they were acting for saw
 * exactly that. Two views of one booking that disagree is worse than either view alone.
 *
 * Kept as a copy rather than extracted to @adotel/components: the two apps release
 * independently, and a shared component would couple their deploys for a presentational
 * gain. If a third consumer appears, extract it then.
 */
type PromotionSummaryProps = {
  application?: PromotionApplication | null
}

function stateMessage(application: PromotionApplication): {
  key: string
  warning?: boolean
} {
  switch (application.state) {
    case 'NoEligiblePromotion':
      return { key: 'No eligible promotion was found for this booking.' }
    case 'EvaluationUnavailable':
      return {
        key: 'Promotion evaluation is temporarily unavailable. The shown price does not include promotion savings.',
        warning: true
      }
    case 'ReleasePending':
      return {
        key: 'Promotion release is pending. The booking may be recalculated.',
        warning: true
      }
    case 'Released':
      return { key: 'The promotion is no longer applied to this booking.' }
    case 'Confirmed':
      return { key: 'Promotion confirmed for this booking.' }
    case 'Reserved':
      return { key: 'Promotion reserved for this booking.' }
    default:
      // An unrecognised state must not ASSERT that a promotion applied. Saying
      // "applied" beside an empty list tells an operator a discount is on the booking
      // when none is — the one reading of this panel that is worse than saying nothing.
      // A state we do not know is a state we report as unknown.
      return application.applied.length > 0
        ? { key: 'Promotion applied to this booking.' }
        : {
            key: 'Promotion status for this booking is unknown.',
            warning: true
          }
  }
}

/**
 * `1,250 IRR` — the amount and its unit, which appear together everywhere in this card and
 * were written out three times before. Kept as a function rather than a component so it can
 * sit inside a template string alongside other text.
 */
function amountWithCurrency(minorAmount: string, currency: string): string {
  return `${formatPromotionMinorAmount(minorAmount, currency)} ${getTranslate(
    currency
  )}`
}

/**
 * One applied promotion and the room stays it landed on.
 *
 * Extracted so PromotionSummary stays readable, not to make it reusable — it has exactly
 * one caller and is not exported. The allocation currency falls back to the promotion's:
 * the service omits it when it matches, and a missing unit beside an amount is worse than a
 * repeated one.
 */
const PromotionRow = ({ promotion }: { promotion: AppliedPromotion }) => {
  const classes = useStyles()

  return (
    <div className={classes.promotion}>
      <div className={classes.row}>
        <div className={classes.details}>
          <Typography variant="h5" className={classes.title}>
            {promotion.name}
          </Typography>
          <Typography variant="h6" className={classes.message}>
            {getTranslate(
              promotion.distribution === 'CodeEntry'
                ? 'Promotion code applied'
                : 'Automatic promotion'
            )}
          </Typography>
        </div>
        <Typography variant="h6" className={classes.amount}>
          {amountWithCurrency(promotion.discountMinor, promotion.currency)}
        </Typography>
      </div>
      {(promotion.allocations ?? []).map((allocation) => (
        <div className={classes.row} key={allocation.roomStayReference}>
          <Typography variant="h6" className={classes.allocation}>
            {getTranslate('Room stay {reference}', {
              reference: allocation.roomStayReference
            })}
          </Typography>
          <Typography variant="h6" className={classes.allocation}>
            {amountWithCurrency(
              allocation.discountMinor,
              allocation.currency ?? promotion.currency
            )}
          </Typography>
        </div>
      ))}
    </div>
  )
}

/**
 * The card's bottom line. Rendered only when something actually applied — a "savings" row
 * reading zero next to no promotions invites the reader to hunt for a discount that was
 * never there.
 */
const SavingsTotal = ({
  totalDiscountMinor,
  currency
}: {
  totalDiscountMinor: string
  currency: string
}) => {
  const classes = useStyles()

  return (
    <>
      <Divider className={classes.divider} />
      <div className={classes.row}>
        <Typography variant="h5" className={classes.title}>
          {getTranslate('Promotion savings')}
        </Typography>
        <Typography variant="h5" className={classes.amount}>
          {amountWithCurrency(totalDiscountMinor, currency)}
        </Typography>
      </div>
    </>
  )
}

const PromotionSummary = ({ application }: PromotionSummaryProps) => {
  const classes = useStyles()

  if (
    !application ||
    (application.applied.length === 0 && application.state === undefined)
  ) {
    return null
  }

  const status = stateMessage(application)
  const hasAppliedPromotions = application.applied.length > 0

  return (
    <section className={classes.card} aria-label={getTranslate('Promotions')}>
      <div className={classes.header}>
        <LocalOfferOutlinedIcon className={classes.icon} />
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Promotions')}
        </Typography>
      </div>
      <Typography
        variant="h6"
        className={[
          classes.message,
          status.warning ? classes.warning : '',
          hasAppliedPromotions ? classes.success : ''
        ].join(' ')}>
        {getTranslate(status.key)}
      </Typography>

      {application.applied.map((promotion) => (
        <PromotionRow key={promotion.promotionId} promotion={promotion} />
      ))}

      {hasAppliedPromotions && (
        <SavingsTotal
          totalDiscountMinor={application.totalDiscountMinor}
          currency={application.currency}
        />
      )}
    </section>
  )
}

export default PromotionSummary
